type TErrorConfig = {
  userMessage: string;
  originalErrorKey?: string;
};

type TErrorPattern = {
  pattern: RegExp | ((error: string) => boolean);
  config: TErrorConfig;
};

const ERROR_PATTERNS: TErrorPattern[] = [
  {
    pattern: (error: string) =>
      error.toLowerCase().includes("project is being set up") ||
      error.toLowerCase().includes("chapter generation and updates are not available"),
    config: {
      userMessage:
        "Chapter generation and updates are not available while the project is being set up. Please wait for the setup to complete, or use the chat for general questions.",
      originalErrorKey: "PROJECT_SETUP_IN_PROGRESS"
    }
  },
  {
    pattern: (error: string) =>
      error.toLowerCase().includes("maximum context length") ||
      error.toLowerCase().includes("input exceeds the context window of this model"),
    config: {
      userMessage: "During chapter generation, one of the steps failed. Our team is already looking into it.",
      originalErrorKey: "CONTEXT_TOO_BIG"
    }
  },
  {
    pattern: (error: string) => {
      const lowerError = error.toLowerCase();
      return (
        lowerError.includes("rate limit") ||
        lowerError.includes("too many requests") ||
        lowerError.includes("429") ||
        lowerError.includes("usage limit")
      );
    },
    config: {
      userMessage: "I'm getting a bit overloaded right now — please wait a few seconds and try again.",
      originalErrorKey: "RATE_LIMIT"
    }
  },
  {
    pattern: (error: string) =>
      error.toLowerCase().includes("took too long") || error.toLowerCase().includes("timed out"),
    config: {
      userMessage: "This one's taking longer than expected. Please try again or split your request into smaller parts.",
      originalErrorKey: "TIMEOUT"
    }
  },
  {
    pattern: (error: string) => error.toLowerCase().includes("timeout"),
    config: {
      userMessage: "This one's taking longer than expected. Please try again or split your request into smaller parts.",
      originalErrorKey: "TIMEOUT"
    }
  },
  {
    pattern: (error: string) => error.toLowerCase().includes("json") && error.toLowerCase().includes("parse"),
    config: {
      userMessage:
        "I couldn't process the answer correctly. Try rephrasing your last message or splitting it into smaller parts.",
      originalErrorKey: "CORRUPTED_JSON"
    }
  },
  {
    pattern: (error: string) =>
      error.toLowerCase().includes("model is overloaded") ||
      error.toLowerCase().includes("you exceeded your current quota"),
    config: {
      userMessage: "I'm getting a bit overloaded right now — please wait a few seconds and try again.",
      originalErrorKey: "RATE_LIMIT"
    }
  },
  {
    pattern: (error: string) => error.toLowerCase().includes("not found"),
    config: {
      userMessage: "Hmm... Seems like the template configuration is incorrect. Our team will investigate it.",
      originalErrorKey: "INCORRECT_CONFIG"
    }
  }
];

export function getErrorConfig(
  error: string,
  isLabsMode = false
): {
  userMessage: string;
  originalError?: string;
  errorKey?: string;
} {
  const matchingPattern = ERROR_PATTERNS.find((pattern) => {
    if (typeof pattern.pattern === "function") return pattern.pattern(error);

    return pattern.pattern.test(error);
  });

  const config = matchingPattern?.config || {
    userMessage: "Something went wrong. Please try again.",
    originalErrorKey: "GENERIC_ERROR"
  };

  if (isLabsMode && config.originalErrorKey) console.error(`[${config.originalErrorKey}] Original error:`, error);

  return {
    userMessage: config.userMessage,
    originalError: isLabsMode ? error : undefined,
    errorKey: config.originalErrorKey
  };
}
